// Home background: how the photo fits and which part always stays visible when it is cropped (moved from HomeScreen, Tailwind).
import React, { useRef } from 'react';
import { useS3Image } from '../../hooks/useS3Image';

export interface BackgroundPatch { fit?: 'cover' | 'contain'; focal_x?: number; focal_y?: number }

const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

const CropBox: React.FC<{ label: string; w: number; h: number; url?: string | null; x: number; y: number }> = ({ label, w, h, url, x, y }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className="rounded-lg border border-gray-200"
      style={{ width: (110 * w) / h, height: 110, background: url ? `url(${url}) ${x}% ${y}%/cover no-repeat` : '#F3F4F6' }}
    />
    <div className="text-[10px] font-medium text-gray-500">{label}</div>
  </div>
);

export const BackgroundFocalEditor: React.FC<{
  imageKey: string;
  fit: 'cover' | 'contain';
  focalX: number;
  focalY: number;
  onChange: (patch: BackgroundPatch) => void;
  disabled?: boolean;
}> = ({ imageKey, fit, focalX, focalY, onChange, disabled }) => {
  const { imageUrl } = useS3Image(imageKey);
  const stage = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const move = (cx: number, cy: number) => {
    const el = stage.current;
    if (!el || disabled) return;
    const r = el.getBoundingClientRect();
    onChange({ focal_x: clamp(((cx - r.left) / r.width) * 100), focal_y: clamp(((cy - r.top) / r.height) * 100) });
  };

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-gray-500">Image fit</span>
        <div className="flex gap-2">
          {(['cover', 'contain'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              aria-pressed={fit === opt}
              disabled={disabled}
              onClick={() => onChange({ fit: opt })}
              className="flex-1 rounded-lg border px-3 py-2 text-[13px] font-semibold disabled:opacity-60"
              style={fit === opt ? { background: 'var(--tenant-primary, #0B5C4D)', color: '#fff', borderColor: 'var(--tenant-primary, #0B5C4D)' } : { background: '#fff', color: '#374151', borderColor: '#D1D5DB' }}
            >
              {opt === 'cover' ? 'Cover (crop)' : 'Contain (fit)'}
            </button>
          ))}
        </div>
      </div>

      {fit === 'cover' && (
        <>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-gray-500">Focal point</span>
            <span className="text-[11px] text-gray-500">Drag the dot to the area you want to always keep visible when the image is cropped.</span>
            <div
              ref={stage}
              role="presentation"
              onPointerDown={(e) => { dragging.current = true; (e.target as HTMLElement).setPointerCapture?.(e.pointerId); move(e.clientX, e.clientY); }}
              onPointerMove={(e) => { if (dragging.current) move(e.clientX, e.clientY); }}
              onPointerUp={() => { dragging.current = false; }}
              onPointerCancel={() => { dragging.current = false; }}
              className="relative w-full select-none overflow-hidden rounded-[10px]"
              style={{ aspectRatio: '16 / 9', cursor: 'crosshair', touchAction: 'none', background: imageUrl ? `url(${imageUrl}) center/contain no-repeat var(--tenant-primary, #111827)` : '#F3F4F6' }}
            >
              <div className="pointer-events-none absolute inset-x-0" style={{ top: `${focalY}%`, height: 1, background: 'rgba(255,255,255,.6)', boxShadow: '0 0 0 1px rgba(0,0,0,.2)' }} />
              <div className="pointer-events-none absolute inset-y-0" style={{ left: `${focalX}%`, width: 1, background: 'rgba(255,255,255,.6)', boxShadow: '0 0 0 1px rgba(0,0,0,.2)' }} />
              <div className="pointer-events-none absolute h-[22px] w-[22px] rounded-full bg-white" style={{ left: `${focalX}%`, top: `${focalY}%`, transform: 'translate(-50%,-50%)', border: '3px solid var(--tenant-primary, #111827)', boxShadow: '0 2px 8px rgba(0,0,0,.4)' }} />
            </div>
            <span className="font-mono text-[11px] text-gray-400">X: {focalX}%  Y: {focalY}%</span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-500">Crop preview</span>
            <div className="flex flex-wrap items-start gap-3">
              <CropBox label="Mobile" w={9} h={19} url={imageUrl} x={focalX} y={focalY} />
              <CropBox label="Square" w={1} h={1} url={imageUrl} x={focalX} y={focalY} />
              <CropBox label="Web" w={16} h={9} url={imageUrl} x={focalX} y={focalY} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
